import { Preferences } from "@capacitor/preferences";
import {
  IonAvatar,
  IonButton,
  IonContent,
  IonIcon,
  IonImg,
  IonPage,
  IonRow,
  IonToast,
} from "@ionic/react";
import * as Sentry from "@sentry/react";
import {
  alertCircleOutline,
  arrowForwardOutline,
  qrCodeOutline,
} from "ionicons/icons";
import { useState } from "react";
import { useHistory } from "react-router";
import { useGetCurrentJourneyQuery } from "../../api/journey";
import { useGetMeQuery } from "../../api/user";
import { getVehicleById } from "../../api/vehicles";
import backgroudVehicleImage from "../../assets/image/backgroundVehicule.png";
import CarrierRightSvg from "../../assets/svg/carrierRightFullOpacity.svg";
import TractorRightSvg from "../../assets/svg/tractorRightFullOpacity.svg";
import { useToast } from "../../components/ToastContext";
import { ApiError } from "../../types/api/api.types";
import { Vehicle } from "../../types/api/vehicle.types";
import { BarcodeScanError, runBarcodeScan } from "../../utils/barcode";
import "./IdentificationVehicle.css";

const IdentificationVehicle: React.FC = () => {
  const history = useHistory();
  const { showToast } = useToast();
  const { data: currentJourney } = useGetCurrentJourneyQuery();

  if (currentJourney?.id) {
    Preferences.get({ key: "journey" }).then((response) => {
      if (response.value) {
        history.push(`/inspection/${currentJourney.id}/finish`);
      }
    });
  } else {
    Preferences.remove({ key: "damages" });
  }

  const [scanResult, setScanResult] = useState<any | null>(null);
  const [toastAdviceIsOpen, setToastAdviceIsOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const { data: driver, isLoading } = useGetMeQuery();

  const scanBarcode = async () => {
    try {
      const code = await runBarcodeScan();
      const response = await getVehicleById(code);

      if (response && (response as ApiError).error) {
        return showToast(`${(response as ApiError).error.message}`);
      }

      if (!(response as Vehicle).active) {
        setToastMessage("Véhicule invalide");
        setToastAdviceIsOpen(true);
      } else if ((response as Vehicle).type.code === "Trailer") {
        setToastMessage(
          "Vous avez scanné une semi-remorque, veuillez commencer par le tracteur",
        );
        setToastAdviceIsOpen(true);
      } else if ((response as Vehicle).immobilized) {
        setToastMessage("Véhicule immobilisé pour réparation");
        setToastAdviceIsOpen(true);
      } else {
        setScanResult(response as Vehicle);
      }
    } catch (error) {
      if (error instanceof BarcodeScanError) {
        showToast(error.message);
      } else {
        Sentry.captureException(error);
        showToast("Une erreur inattendue est survenue. Veuillez réessayer.");
      }
    }
  };

  const setJourney = async () => {
    await Preferences.set({
      key: "journey",
      value: JSON.stringify({
        mainVehicleId: scanResult.id,
        driverId: driver.user.id,
        trailerId: null,
      }),
    });
  };

  return (
    <IonPage>
      <IonToast
        isOpen={toastAdviceIsOpen}
        message={toastMessage || ""}
        onDidDismiss={() => setToastAdviceIsOpen(false)}
        position="top"
        duration={20000}
        icon={alertCircleOutline}
        className="IdentificationVehicle-toast"
        buttons={[
          {
            text: "Fermer",
            role: "cancel",
          },
        ]}
      ></IonToast>
      <IonContent fullscreen className="IdentificationVehicle">
        <IonRow className="IdentificationVehicle-subtitle">
          Nouveau trajet
        </IonRow>
        <IonRow className="IdentificationVehicle-title">
          Identification véhicule
        </IonRow>
        <div className="IdentificationVehicle-description">
          Pour commencer, vous devez{" "}
          <span className="IdentificationVehicle-description_highlight">
            {" "}
            scanner le QR code
          </span>{" "}
          qui se trouve à l'intérieur de votre véhicule pour identifier
          celui-ci.
        </div>
        <IonButton
          color="success"
          onClick={async () => await scanBarcode()}
          expand="block"
          className="IdentificationVehicle-button-item"
        >
          <div className="IdentificationVehicle-button">
            <div className="IdentificationVehicle-button-content">
              <div className="IdentificationVehicle-button-logo">
                <IonIcon
                  icon={qrCodeOutline}
                  className="IdentificationVehicle-button-logo-icon"
                />
              </div>
              <div className="IdentificationVehicle-button-content-text">
                Scanner le QR code
              </div>
            </div>
            <IonIcon
              icon={arrowForwardOutline}
              className="IdentificationVehicle-button-icon"
            />
          </div>
        </IonButton>

        {scanResult?.id ? (
          <IonButton
            color="warning"
            routerLink={`/inspection/${scanResult.id}/${scanResult.type.code}/Document`}
            routerDirection="forward"
            expand="block"
            className="IdentificationVehicle-button-item"
            onClick={async () => {
              await setJourney();
            }}
          >
            <div className="IdentificationVehicle-button">
              <div className="IdentificationVehicle-button-content">
                <IonAvatar className="IdentificationVehicle-button-content-avatar">
                  <IonIcon
                    src={
                      scanResult.type.code === "Carrier"
                        ? CarrierRightSvg
                        : TractorRightSvg
                    }
                    className="IdentificationVehicle-button-content-avatar-icon"
                  />
                </IonAvatar>
                <div className="IdentificationVehicle-button-content-text">
                  {scanResult.brand}
                  <br />
                  {scanResult.registrationNumber}
                  <br />
                  {scanResult.internalCode}
                </div>
              </div>
              <IonIcon
                icon={arrowForwardOutline}
                className="IdentificationVehicle-button-icon"
              />
            </div>
          </IonButton>
        ) : null}
        <div className="IdentificationVehicle-footer">
          <IonImg
            src={backgroudVehicleImage}
            alt="Background image"
            className="IdentificationVehicle-footer-image"
          />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default IdentificationVehicle;
